import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="theme-preview"
export default class extends Controller {
  static targets = [
    "previewButton", "previewLink", "previewBadge", "previewProgress",
    "previewButtonDark", "previewLinkDark", "previewBadgeDark", "previewProgressDark",
    "lightModePreview", "darkModePreview"
  ]

  connect() {
    console.log("ThemePreviewController connected")
    // Temporary alert for debugging - remove after confirming it works
    // alert("Theme preview controller connected!")
    this.initializePreviews()
  }

  initializePreviews() {
    // Initialize primary color preview
    const primaryRadio = this.element.querySelector('input[name="primary_color"]:checked')
    if (primaryRadio) {
      this.updatePrimaryColorPreview(primaryRadio.value)
    }

    // Initialize light background preview
    const lightBgRadio = this.element.querySelector('input[name="light_mode_background_color"]:checked')
    if (lightBgRadio) {
      this.updateLightBackgroundPreview(lightBgRadio.value)
    }

    // Initialize dark background preview
    const darkBgRadio = this.element.querySelector('input[name="dark_mode_background_color"]:checked')
    if (darkBgRadio) {
      this.updateDarkBackgroundPreview(darkBgRadio.value)
    }
  }

  // Toggle between light and dark mode preview
  togglePreviewMode(event) {
    const isDarkMode = event.target.checked
    console.log("Toggle preview mode called, isDarkMode:", isDarkMode)

    // Try using targets first
    let lightPreview = this.hasLightModePreviewTarget ? this.lightModePreviewTarget : null
    let darkPreview = this.hasDarkModePreviewTarget ? this.darkModePreviewTarget : null

    // Fallback to querySelector if targets not found
    if (!lightPreview) {
      lightPreview = this.element.querySelector('[data-theme-preview-target="lightModePreview"]')
      console.log("Using querySelector for lightPreview:", lightPreview)
    }
    if (!darkPreview) {
      darkPreview = this.element.querySelector('[data-theme-preview-target="darkModePreview"]')
      console.log("Using querySelector for darkPreview:", darkPreview)
    }

    if (lightPreview && darkPreview) {
      if (isDarkMode) {
        lightPreview.classList.add('hidden')
        darkPreview.classList.remove('hidden')
        console.log("Switched to dark mode")
      } else {
        lightPreview.classList.remove('hidden')
        darkPreview.classList.add('hidden')
        console.log("Switched to light mode")
      }
    } else {
      console.log("Could not find preview elements - light:", lightPreview, "dark:", darkPreview)
    }
  }

  // Primary color methods
  updatePreview(event) {
    console.log("updatePreview called with:", event.target.value)
    const color = event.target.value
    this.updatePrimaryColorPreview(color)
    this.syncPrimaryColorInputs(color)
  }

  syncCustomColor(event) {
    const color = event.target.value
    console.log("syncCustomColor:", color)
    this.updatePrimaryColorPreview(color)
    document.getElementById('custom_color_hex').value = color
    this.uncheckRadios('primary_color')
  }

  syncHexInput(event) {
    let color = event.target.value
    if (color && !color.startsWith('#')) {
      color = '#' + color
    }
    if (color.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)) {
      this.updatePrimaryColorPreview(color)
      document.getElementById('custom_color_picker').value = color
      this.uncheckRadios('primary_color')
    }
  }

  updatePrimaryColorPreview(color) {
    console.log("Updating primary color preview to:", color)
    // Light mode elements
    if (this.hasPreviewButtonTarget) this.previewButtonTarget.style.backgroundColor = color
    if (this.hasPreviewLinkTarget) this.previewLinkTarget.style.color = color
    if (this.hasPreviewBadgeTarget) this.previewBadgeTarget.style.backgroundColor = color
    if (this.hasPreviewProgressTarget) this.previewProgressTarget.style.backgroundColor = color

    // Dark mode elements
    if (this.hasPreviewButtonDarkTarget) this.previewButtonDarkTarget.style.backgroundColor = color
    if (this.hasPreviewLinkDarkTarget) this.previewLinkDarkTarget.style.color = color
    if (this.hasPreviewBadgeDarkTarget) this.previewBadgeDarkTarget.style.backgroundColor = color
    if (this.hasPreviewProgressDarkTarget) this.previewProgressDarkTarget.style.backgroundColor = color
  }

  syncPrimaryColorInputs(color) {
    const picker = document.getElementById('custom_color_picker')
    const hex = document.getElementById('custom_color_hex')
    if (picker) picker.value = color
    if (hex) hex.value = color
  }

  // Light mode background methods
  updateLightBackground(event) {
    console.log("updateLightBackground called with:", event.target.value)
    const color = event.target.value
    this.updateLightBackgroundPreview(color)
    this.syncLightBgInputs(color)
  }

  syncLightBgColor(event) {
    const color = event.target.value
    this.updateLightBackgroundPreview(color)
    document.getElementById('custom_light_bg_hex').value = color
    this.uncheckRadios('light_mode_background_color')
  }

  syncLightBgHexInput(event) {
    let color = event.target.value
    if (color && !color.startsWith('#')) {
      color = '#' + color
    }
    if (color.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)) {
      this.updateLightBackgroundPreview(color)
      document.getElementById('custom_light_bg_picker').value = color
      this.uncheckRadios('light_mode_background_color')
    }
  }

  updateLightBackgroundPreview(color) {
    console.log("Updating light background to:", color)
    if (this.hasLightModePreviewTarget) {
      this.lightModePreviewTarget.style.backgroundColor = color
    }
  }

  syncLightBgInputs(color) {
    const picker = document.getElementById('custom_light_bg_picker')
    const hex = document.getElementById('custom_light_bg_hex')
    if (picker) picker.value = color
    if (hex) hex.value = color
  }

  // Dark mode background methods
  updateDarkBackground(event) {
    console.log("updateDarkBackground called with:", event.target.value)
    const color = event.target.value
    this.updateDarkBackgroundPreview(color)
    this.syncDarkBgInputs(color)
  }

  syncDarkBgColor(event) {
    const color = event.target.value
    this.updateDarkBackgroundPreview(color)
    document.getElementById('custom_dark_bg_hex').value = color
    this.uncheckRadios('dark_mode_background_color')
  }

  syncDarkBgHexInput(event) {
    let color = event.target.value
    if (color && !color.startsWith('#')) {
      color = '#' + color
    }
    if (color.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)) {
      this.updateDarkBackgroundPreview(color)
      document.getElementById('custom_dark_bg_picker').value = color
      this.uncheckRadios('dark_mode_background_color')
    }
  }

  updateDarkBackgroundPreview(color) {
    console.log("Updating dark background to:", color)
    if (this.hasDarkModePreviewTarget) {
      this.darkModePreviewTarget.style.backgroundColor = color
    }
  }

  syncDarkBgInputs(color) {
    const picker = document.getElementById('custom_dark_bg_picker')
    const hex = document.getElementById('custom_dark_bg_hex')
    if (picker) picker.value = color
    if (hex) hex.value = color
  }

  // Helper to uncheck radio buttons
  uncheckRadios(name) {
    this.element.querySelectorAll(`input[name="${name}"]`).forEach(radio => {
      radio.checked = false
    })
  }
}
